"use client";

import { renderMark, renderText } from "@2u-landing-pages/utils/contentful-richtext";
import EventClient from "@2u-landing-pages/utils/event-client";
import { documentToPlainTextString } from "@contentful/rich-text-plain-text-renderer";
import { documentToReactComponents } from "@contentful/rich-text-react-renderer";
import { BLOCKS, INLINES } from "@contentful/rich-text-types";
import { Body } from "@red-digital/bricks";
import React, { useCallback, useEffect, useId, useRef, useState } from "react";
import { Tooltip as ReactTooltip } from "react-tooltip";
import { Icon } from "../Icon";
import { TooltipEventData, TooltipProps } from "./TooltipTypes";
// @ts-ignore
import styles from "./Tooltip.module.scss";

export const Tooltip = ({
  body,
  icon_size = "sm",
  position = "top",
  location,
  parentContent,
  ariaLabel,
  phoneNumber,
  parseMetadata,
}: TooltipProps) => {
  const tooltipId = useId();

  // States for keyboard and mouse interactions
  const [isKeyboardFocused, setIsKeyboardFocused] = useState(false);
  const [isMouseOver, setIsMouseOver] = useState(false);
  const [isHoveringTooltip, setIsHoveringTooltip] = useState(false);
  const tooltipTriggerRef = useRef<HTMLButtonElement>(null);
  const tooltipRef = useRef<HTMLDivElement>(null);
  const mouseLeaveTimeoutRef = useRef<number>();

  // Combined state for aria attributes and event tracking
  const isTooltipVisible = isKeyboardFocused || isMouseOver;

  const handleKeyDown = useCallback(
    (event: KeyboardEvent) => {
      if (event.key === "Escape" && isKeyboardFocused) {
        setIsKeyboardFocused(false);
        tooltipTriggerRef.current?.focus();
      }
    },
    [isKeyboardFocused]
  );

  useEffect(() => {
    document.addEventListener("keydown", handleKeyDown);
    return () => {
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [handleKeyDown]);

  // Focus management
  useEffect(() => {
    const handleFocusIn = (event: FocusEvent) => {
      const target = event.target as HTMLElement;
      const tooltipElement = document.getElementById(tooltipId);
      if (tooltipTriggerRef.current?.contains(target) || tooltipElement?.contains(target)) {
        setIsKeyboardFocused(true);
        // Clear any pending mouse timeouts when switching to keyboard
        if (mouseLeaveTimeoutRef.current) {
          window.clearTimeout(mouseLeaveTimeoutRef.current);
          mouseLeaveTimeoutRef.current = undefined;
        }
      }
    };

    const handleFocusOut = (event: FocusEvent) => {
      const relatedTarget = event.relatedTarget as HTMLElement;
      const tooltipElement = document.getElementById(tooltipId);
      if (!tooltipTriggerRef.current?.contains(relatedTarget) && !tooltipElement?.contains(relatedTarget)) {
        setIsKeyboardFocused(false);
      }
    };

    document.addEventListener("focusin", handleFocusIn);
    document.addEventListener("focusout", handleFocusOut);

    return () => {
      document.removeEventListener("focusin", handleFocusIn);
      document.removeEventListener("focusout", handleFocusOut);
    };
  }, [tooltipId]);

  const clearTimeouts = () => {
    if (mouseLeaveTimeoutRef.current) {
      window.clearTimeout(mouseLeaveTimeoutRef.current);
      mouseLeaveTimeoutRef.current = undefined;
    }
  };

  const handleTriggerClick = () => {
    if (isTooltipVisible) {
      setIsKeyboardFocused(false);
      setIsMouseOver(false);
    } else {
      handleShowTooltip(false);
    }
  };

  const handleShowTooltip = (isMouseEvent: boolean = false) => {
    clearTimeouts();

    if (isMouseEvent) {
      setIsMouseOver(true);
    } else {
      setIsKeyboardFocused(true);
    }

    // Track event
    const eventData: TooltipEventData = {
      webElement: {
        elementType: "Landing Page",
        htmlId: window.location.href,
        location: location ? `${location} - Tooltip` : "Tooltip",
        name: parentContent ? documentToPlainTextString(parentContent) : "",
        text: documentToPlainTextString(body),
      },
    };

    EventClient.elementViewed(eventData);
  };

  const handleTriggerMouseLeave = () => {
    // Start a timeout to close the tooltip if the user doesn't enter the tooltip content
    mouseLeaveTimeoutRef.current = window.setTimeout(() => {
      if (!isHoveringTooltip) {
        setIsMouseOver(false);
      }
    }, 100); // Small delay to allow movement to tooltip
  };

  const handleTooltipMouseEnter = () => {
    clearTimeouts();
    setIsHoveringTooltip(true);
    setIsMouseOver(true);
  };

  const handleTooltipMouseLeave = () => {
    setIsHoveringTooltip(false);
    setIsMouseOver(false);
  };

  const handleExternalLinkClick = (event: React.MouseEvent<HTMLAnchorElement>) => {
    event.stopPropagation();

    const eventData: TooltipEventData = {
      webElement: {
        elementType: "Landing Page",
        htmlId: event.currentTarget.href,
        location: location ? `${location} - Tooltip` : "Tooltip",
        name: event.currentTarget.innerText,
        position: "",
        text: "",
      },
    };

    EventClient.elementClicked(eventData);
  };

  const renderOptions = {
    renderMark,
    renderText: (text: string) => renderText(parseMetadata?.(text) || text, phoneNumber),
    renderNode: {
      [BLOCKS.PARAGRAPH]: (_node: any, children: React.ReactNode) => <Body variant="caption-lg">{children}</Body>,
      [INLINES.HYPERLINK]: (node: any) => {
        const { uri } = node?.data;
        const content = node?.content?.[0];
        if (content && "value" in content) {
          const isExternal = uri.startsWith("http") || uri.startsWith("//");
          return (
            <span>
              <a
                href={uri}
                target={isExternal ? "_blank" : undefined}
                rel={isExternal ? "noopener noreferrer" : undefined}
                onClick={handleExternalLinkClick}
                className={styles.tooltipLink}
                tabIndex={isTooltipVisible ? 0 : -1}
              >
                {content.value}
                {isExternal && <span className={styles.screenReaderOnly}>(opens in new tab)</span>}
              </a>
            </span>
          );
        }
        return null;
      },
    },
  };

  const tooltipContentId = `tooltip-content-${tooltipId}`;
  const defaultAriaLabel = "More information";

  return (
    <>
      <button
        ref={tooltipTriggerRef}
        className={styles.tooltip_icon}
        data-tooltip-id={tooltipId}
        onTouchEnd={handleTriggerClick}
        onFocus={() => handleShowTooltip(false)}
        onBlur={() => setIsKeyboardFocused(false)}
        onMouseEnter={() => handleShowTooltip(true)}
        onMouseLeave={handleTriggerMouseLeave}
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            handleShowTooltip(false);
          }
        }}
        aria-expanded={isTooltipVisible}
        aria-describedby={isTooltipVisible ? tooltipContentId : undefined}
        aria-label={ariaLabel || defaultAriaLabel}
        type="button"
        tabIndex={0}
      >
        <sup>
          <Icon name="info" size={icon_size} aria-hidden="true" />
        </sup>
      </button>

      <ReactTooltip
        id={tooltipId}
        place={position}
        className={styles.tooltip}
        clickable={true}
        isOpen={isTooltipVisible}
        delayHide={250}
        role="tooltip"
        aria-live="polite"
        afterShow={() => {
          const tooltipElement = document.getElementById(tooltipId);
          if (tooltipElement) {
            tooltipElement.addEventListener("mouseenter", handleTooltipMouseEnter);
            tooltipElement.addEventListener("mouseleave", handleTooltipMouseLeave);
          }
        }}
        afterHide={() => {
          const tooltipElement = document.getElementById(tooltipId);
          if (tooltipElement) {
            tooltipElement.removeEventListener("mouseenter", handleTooltipMouseEnter);
            tooltipElement.removeEventListener("mouseleave", handleTooltipMouseLeave);
          }
        }}
      >
        <span id={tooltipContentId} ref={tooltipRef}>
          {documentToReactComponents(body, renderOptions)}
        </span>
      </ReactTooltip>
    </>
  );
};
